
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSession } from '@/lib/auth-client';
import { walletAPI, type WalletTransaction } from '@/lib/api/wallet';

const DEFAULT_LIMIT = 20;

interface UseWalletHistoryReturn {
  transactions: WalletTransaction[];
  isLoading: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
  refetch: () => void;
  loadMore: () => void;
}

export function useWalletHistory(canvasId?: string): UseWalletHistoryReturn {
  const session = useSession();
  const isAuthenticated = !!session.data?.user;

  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  const fetchInFlight = useRef(false);
  const backoffUntilRef = useRef(0);

  const fetchTransactions = useCallback(
    async (reset: boolean) => {
      if (!isAuthenticated || fetchInFlight.current) return;
      // Respect backoff after rate limit (429)
      if (Date.now() < backoffUntilRef.current) return;

      fetchInFlight.current = true;
      setIsLoading(true);
      setError(null);

      const currentOffset = reset ? 0 : offset;

      try {
        const result = await walletAPI.getTransactions(true, {
          canvasId,
          limit: DEFAULT_LIMIT,
          offset: currentOffset,
        });

        if (reset) {
          setTransactions(result.transactions);
        } else {
          setTransactions((prev) => [...prev, ...result.transactions]);
        }

        setTotal(result.total);
        setOffset(currentOffset + result.transactions.length);
      } catch (err) {
        console.error('[WalletHistory] fetch failed:', err);
        // Back off on rate limit (429)
        const errStatus = (err as { status?: number }).status;
        const errMsg = err instanceof Error ? err.message : '';
        if (errStatus === 429 || errMsg.toLowerCase().includes('too many')) {
          backoffUntilRef.current = Date.now() + 120_000;
        }
        setError(
          err instanceof Error ? err.message : 'Failed to load transactions',
        );
      } finally {
        setIsLoading(false);
        fetchInFlight.current = false;
      }
    },
    [isAuthenticated, offset, canvasId],
  );

  // Auto-fetch on mount when authenticated, or when canvasId changes
  useEffect(() => {
    if (!isAuthenticated) return;
    void fetchTransactions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, canvasId]);

  // Listen for balance-changed events to auto-refresh
  useEffect(() => {
    if (!isAuthenticated) return;

    const handler = () => {
      void fetchTransactions(true);
    };

    window.addEventListener('genie:balance-changed', handler);
    return () => {
      window.removeEventListener('genie:balance-changed', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const refetch = useCallback(() => {
    if (isAuthenticated) {
      void fetchTransactions(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadMore = useCallback(() => {
    if (isAuthenticated && !isLoading && transactions.length < total) {
      void fetchTransactions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, transactions.length, total]);

  if (!isAuthenticated) {
    return {
      transactions: [],
      isLoading: false,
      error: null,
      total: 0,
      hasMore: false,
      refetch: () => {},
      loadMore: () => {},
    };
  }

  return {
    transactions,
    isLoading,
    error,
    total,
    hasMore: transactions.length < total,
    refetch,
    loadMore,
  };
}
